import { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import {
  Inbox, FileEdit, ShieldCheck, AlertTriangle, CheckCircle2,
  ExternalLink, Check, X, RefreshCcw, Search, XCircle, Filter, LayoutGrid, List,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDate } from "@/lib/constants";
import DateField from "@/components/DateField";
import ConfirmDialog from "@/components/ConfirmDialog";
import StickyContextBar from "@/components/StickyContextBar";
import TasksPage from "@/pages/TasksPage";

// ---------------------------------------------------------------------------
// Tab config (order matters — first tab is default).
// ---------------------------------------------------------------------------
const TABS = [
  { id: "for_me", label: "Untuk Saya", icon: Inbox, tone: "orange", desc: "Item yang menunggu tindakan Anda" },
  { id: "created_by_me", label: "Dibuat Saya", icon: FileEdit, tone: "slate", desc: "Item yang Anda ajukan / buat" },
  { id: "awaiting_approval", label: "Menunggu Approval", icon: ShieldCheck, tone: "amber", desc: "Anda adalah approver-nya" },
  { id: "overdue", label: "Terlambat", icon: AlertTriangle, tone: "rose", desc: "Melewati tenggat & belum selesai" },
  { id: "done", label: "Selesai", icon: CheckCircle2, tone: "emerald", desc: "Sudah tuntas / terverifikasi" },
];

const TONE_CLS = {
  orange: { active: "border-[#FF5A1F] text-[#FF5A1F]", badge: "bg-[#FF5A1F] text-white" },
  slate:  { active: "border-slate-700 text-slate-700", badge: "bg-slate-600 text-white" },
  amber:  { active: "border-amber-500 text-amber-700", badge: "bg-amber-500 text-white" },
  rose:   { active: "border-rose-500 text-rose-700",   badge: "bg-rose-500 text-white" },
  emerald:{ active: "border-emerald-500 text-emerald-700", badge: "bg-emerald-600 text-white" },
};

const PRIORITY_TONE = {
  Kritis: "bg-rose-600 text-white",
  Tinggi: "bg-orange-500 text-white",
  Sedang: "bg-amber-100 text-amber-800 border border-amber-300",
  Rendah: "bg-slate-100 text-slate-600 border border-slate-300",
};

const SOURCE_BADGE = {
  tasks: "border-sky-400 bg-sky-50 text-sky-800",
  leaves: "border-purple-400 bg-purple-50 text-purple-800",
  petty_cash: "border-emerald-400 bg-emerald-50 text-emerald-800",
  petty_cash_coa: "border-amber-400 bg-amber-50 text-amber-800",
};

// ---------------------------------------------------------------------------
// Action dispatcher — MEMANGGIL API ASLI MODUL SUMBER (no duplicated logic).
// Kalau modul menambah sumber baru (Reminder/Tender), extend map ini SAJA.
// ---------------------------------------------------------------------------
const ACTION_DISPATCH = {
  tasks: {
    approve: (item) => api.patch(`/tasks/${item.id}/status`, { status: "selesai" }),
    reject:  (item, note) => api.patch(`/tasks/${item.id}/status`, { status: "ditolak", note: note || null }),
    revise:  (item, note) => api.patch(`/tasks/${item.id}/status`, { status: "in_progress", note: note || "Minta revisi" }),
    done:    (item) => api.patch(`/tasks/${item.id}/status`, { status: "menunggu_verifikasi" }),
  },
  leaves: {
    approve: (item, note) => (item.status === "pending_delegate"
      ? api.patch(`/leave-requests/${item.id}/delegate-review`, { action: "approve", note: note || null })
      : api.patch(`/leave-requests/${item.id}/review`, { action: "approve", note: note || null })),
    reject:  (item, note) => (item.status === "pending_delegate"
      ? api.patch(`/leave-requests/${item.id}/delegate-review`, { action: "reject", note: note || null })
      : api.patch(`/leave-requests/${item.id}/review`, { action: "reject", note: note || null })),
  },
  petty_cash: {
    approve: (item) => (item.type === "Verifikasi 1 — Realisasi Petty Cash"
      ? api.post(`/petty-cash/${item.id}/verify`)
      : api.patch(`/petty-cash/${item.id}/review`, { action: "approve" })),
    reject:  (item, note) => api.patch(`/petty-cash/${item.id}/review`, { action: "reject", note: note || null }),
    // "Isi Realisasi" → arahkan user ke halaman petty cash (dialog isi realisasi ada di sana).
    done:    (item) => Promise.resolve({ data: { redirect: `/petty-cash?open=${item.id}` } }),
  },
};

const ACTION_LABELS = {
  open: "Buka Detail",
  approve: "Setujui",
  reject: "Tolak",
  revise: "Minta Revisi",
  done: "Tandai Selesai",
};

const ACTION_STYLE = {
  open: "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50",
  approve: "bg-emerald-600 text-white hover:bg-emerald-700",
  reject: "bg-rose-600 text-white hover:bg-rose-700",
  revise: "bg-amber-500 text-white hover:bg-amber-600",
  done: "bg-[#FF5A1F] text-white hover:bg-[#e04a13]",
};

// Actions that need a confirmation note (mandatory for negative flows).
const NOTE_REQUIRED = { reject: true, revise: false };
const NOTE_OPTIONAL = { approve: true };

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function MyWorkPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  // 2026-06-11 (user request): Merge "My Work & Approval" + "Tasks" → satu
  // halaman dengan view switcher. `view=daftar` (approval inbox) | `view=kanban`
  // (Tasks Kanban lama). Sinkron ke URL supaya deep-link stabil & AppShell
  // bisa arahkan lewat /my-work?view=kanban (menggantikan /tasks lama).
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get("view");
  const view = viewParam === "kanban" ? "kanban" : "daftar";
  const setView = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v === "kanban") next.set("view", "kanban"); else next.delete("view");
    setSearchParams(next, { replace: true });
  };
  const [tab, setTab] = useState("for_me");
  // 2026-06-11 (user request): badge jumlah task aktif di tombol "Kanban Task"
  // supaya user tahu ada berapa task tanpa berpindah view. Aktif = status bukan
  // selesai/dibatalkan. Fetch read-only; silent fail (badge cuma indikator).
  const [taskCount, setTaskCount] = useState(0);
  const loadTaskCount = useCallback(async () => {
    try {
      const { data } = await api.get("/tasks");
      setTaskCount((data || []).filter((t) => !["selesai", "dibatalkan"].includes(t.status)).length);
    } catch { /* silent */ }
  }, []);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ for_me: 0, created_by_me: 0, awaiting_approval: 0, overdue: 0, done: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [source, setSource] = useState("");
  const [projectId, setProjectId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [priority, setPriority] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [confirm, setConfirm] = useState({ open: false, item: null, action: null, note: "" });

  const loadCounts = useCallback(async () => {
    try {
      // 149-cx: Kirim filter yang SAMA dengan /items supaya badge & list konsisten
      // (fix: sebelumnya /counts abaikan filter → badge>0 tapi list kosong bila
      // user aktifkan date/source/priority filter tersembunyi).
      const params = {};
      if (source) params.source_type = source;
      if (projectId) params.project_id = projectId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (priority) params.priority = priority;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/my-work/counts", { params });
      setCounts({
        for_me: data.for_me || 0,
        created_by_me: data.created_by_me || 0,
        awaiting_approval: data.awaiting_approval || 0,
        overdue: data.overdue || 0,
        done: data.done || 0,
      });
    } catch { /* silent */ }
  }, [source, projectId, dateFrom, dateTo, priority, q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { tab };
      if (source) params.source_type = source;
      if (projectId) params.project_id = projectId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (priority) params.priority = priority;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/my-work/items", { params });
      setItems(data.items || []);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Gagal memuat My Work");
    } finally {
      setLoading(false);
    }
  }, [tab, source, projectId, dateFrom, dateTo, priority, q]);

  useEffect(() => { load(); loadCounts(); loadTaskCount(); }, [load, loadCounts, loadTaskCount]);
  useEffect(() => {
    const onRefresh = () => { load(); loadCounts(); loadTaskCount(); };
    window.addEventListener("ens:refresh", onRefresh);
    return () => window.removeEventListener("ens:refresh", onRefresh);
  }, [load, loadCounts, loadTaskCount]);

  const uniqueProjects = useMemo(() => {
    const m = new Map();
    for (const it of items) {
      if (it.project_id && !m.has(it.project_id)) m.set(it.project_id, it.project_name || "-");
    }
    return Array.from(m.entries()).map(([id, name]) => ({ id, name }));
  }, [items]);

  const resetFilters = () => { setQ(""); setSource(""); setProjectId(""); setDateFrom(""); setDateTo(""); setPriority(""); };
  const hasFilter = q || source || projectId || dateFrom || dateTo || priority;

  const openDetail = (item) => {
    if (!item.deeplink) return;
    nav(item.deeplink);
  };

  const runAction = async (item, actionId, note = "") => {
    const dispatch = ACTION_DISPATCH[item.source]?.[actionId];
    if (!dispatch) {
      toast.error(`Aksi "${actionId}" belum tersedia untuk sumber ${item.source}`);
      return;
    }
    setBusyId(item.id + ":" + actionId);
    try {
      const res = await dispatch(item, note);
      if (res?.data?.redirect) {
        nav(res.data.redirect);
        return;
      }
      toast.success(`${ACTION_LABELS[actionId]} berhasil`);
      await load();
      await loadCounts();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || `${ACTION_LABELS[actionId]} gagal`);
    } finally {
      setBusyId(null);
    }
  };

  const handleAction = (item, actionId) => {
    if (actionId === "open") return openDetail(item);
    if (NOTE_REQUIRED[actionId] || NOTE_OPTIONAL[actionId] || actionId === "revise") {
      setConfirm({ open: true, item, action: actionId, note: "" });
      return;
    }
    runAction(item, actionId);
  };

  const currentTab = TABS.find((t) => t.id === tab) || TABS[0];

  return (
    <div className="p-6 max-w-[1600px] mx-auto" data-testid="my-work-page">
      {/* Header */}
      <div className="mb-4">
        <p className="overline text-slate-500">Pekerjaan &amp; Tugas Saya</p>
        <h1 className="font-display text-3xl text-[#0B1D3A]">Pusat Pekerjaan Saya</h1>
        <p className="text-sm text-slate-500 mt-1">
          Semua task, permintaan, dan approval dari berbagai modul — terpusat, tanpa duplikasi data. Aksi langsung
          memanggil layanan modul asal.
        </p>
      </div>

      {/* 2026-06-11: View switcher — Daftar Approval vs Kanban Task. */}
      <div className="mb-4 inline-flex border border-slate-300 bg-white" role="tablist" data-testid="mywork-view-switcher">
        {[
          { v: "daftar", label: "Daftar Approval", icon: List, hint: "Inbox item dari semua modul (task, cuti, petty cash)" },
          { v: "kanban", label: "Kanban Task", icon: LayoutGrid, hint: "Papan Kanban untuk task pribadi & tim", badge: taskCount },
        ].map((o) => (
          <button
            key={o.v}
            type="button"
            onClick={() => setView(o.v)}
            data-testid={`mywork-view-${o.v}`}
            title={o.hint}
            className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-1.5 border-r border-slate-300 last:border-r-0 transition-colors ${view === o.v ? "bg-[#0B1D3A] text-white" : "text-slate-600 hover:bg-slate-50"}`}
          >
            <o.icon size={13} /> {o.label}
            {o.badge > 0 && (
              <span data-testid={`mywork-view-badge-${o.v}`} className={`px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${view === o.v ? "bg-[#FF5A1F] text-white" : "bg-slate-200 text-slate-600"}`}>
                {o.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {view === "kanban" ? (
        <div data-testid="mywork-kanban-embed"><TasksPage /></div>
      ) : (
        <MyWorkDaftar
          tab={tab} setTab={setTab}
          items={items} counts={counts} loading={loading}
          q={q} setQ={setQ} source={source} setSource={setSource}
          projectId={projectId} setProjectId={setProjectId}
          dateFrom={dateFrom} setDateFrom={setDateFrom}
          dateTo={dateTo} setDateTo={setDateTo}
          priority={priority} setPriority={setPriority}
          busyId={busyId} handleAction={handleAction}
          uniqueProjects={uniqueProjects} resetFilters={resetFilters} hasFilter={hasFilter}
          confirm={confirm} setConfirm={setConfirm} runAction={runAction}
        />
      )}
    </div>
  );
}

/**
 * MyWorkDaftar — konten "Daftar Approval" (bekas isi utama MyWorkPage sebelum
 * merge dgn TasksPage). Diekstrak jadi komponen supaya switcher view tetap
 * hidup lintas mode tanpa remount data-loading state.
 */
function MyWorkDaftar({
  tab, setTab, items, counts, loading,
  q, setQ, source, setSource, projectId, setProjectId,
  dateFrom, setDateFrom, dateTo, setDateTo, priority, setPriority,
  busyId, handleAction, uniqueProjects, resetFilters, hasFilter,
  confirm, setConfirm, runAction,
}) {
  const currentTab = TABS.find((t) => t.id === tab) || TABS[0];
  return (
    <>
      <StickyContextBar
        segments={["Pekerjaan Saya", currentTab.label, `${items.length} item`]}
        extra={<span className="text-slate-400 normal-case">{source ? source : "Semua Tipe"}</span>}
      />

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-4 overflow-x-auto">
        <div className="flex items-center gap-1 min-w-max">
          {TABS.map((t) => {
            const active = t.id === tab;
            const tone = TONE_CLS[t.tone] || TONE_CLS.slate;
            const n = counts[t.id] || 0;
            return (
              <button
                key={t.id}
                type="button"
                data-testid={`mywork-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                title={t.desc}
                className={`px-4 py-2.5 text-xs uppercase tracking-wider font-semibold border-b-2 inline-flex items-center gap-2 transition-colors ${
                  active ? tone.active : "border-transparent text-slate-500 hover:text-[#0B1D3A]"
                }`}
              >
                <t.icon size={14} />
                {t.label}
                {n > 0 && (
                  <span className={`px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${active ? tone.badge : "bg-slate-200 text-slate-600"}`}>
                    {n}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-slate-200 p-3 mb-3 flex items-center gap-2 flex-wrap" data-testid="mywork-filter-bar">
        <div className="relative flex items-center">
          <Search size={13} className="absolute left-2.5 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari judul / project / pembuat…"
            data-testid="mywork-search"
            className="pl-8 pr-8 py-2 border border-slate-300 text-xs w-72 focus:outline-none focus:border-[#FF5A1F] bg-white"
          />
          {q && (
            <button type="button" onClick={() => setQ("")} data-testid="mywork-search-clear" className="absolute right-2 text-slate-400 hover:text-[#FF5A1F]">
              <XCircle size={13} />
            </button>
          )}
        </div>
        <select
          value={source} onChange={(e) => setSource(e.target.value)}
          data-testid="mywork-filter-source"
          className="border border-slate-300 text-xs px-2 py-2 bg-white focus:outline-none focus:border-[#FF5A1F]"
        >
          <option value="">Semua Tipe</option>
          <option value="tasks">Task</option>
          <option value="leaves">Cuti/Izin</option>
          <option value="petty_cash">Petty Cash</option>
        </select>
        {uniqueProjects.length > 0 && (
          <select
            value={projectId} onChange={(e) => setProjectId(e.target.value)}
            data-testid="mywork-filter-project"
            className="border border-slate-300 text-xs px-2 py-2 bg-white focus:outline-none focus:border-[#FF5A1F] max-w-[220px]"
          >
            <option value="">Semua Project</option>
            {uniqueProjects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        <select
          value={priority} onChange={(e) => setPriority(e.target.value)}
          data-testid="mywork-filter-priority"
          className="border border-slate-300 text-xs px-2 py-2 bg-white focus:outline-none focus:border-[#FF5A1F]"
        >
          <option value="">Semua Prioritas</option>
          <option value="Kritis">Kritis</option>
          <option value="Tinggi">Tinggi</option>
          <option value="Sedang">Sedang</option>
          <option value="Rendah">Rendah</option>
        </select>
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-slate-500 uppercase tracking-wider">Masuk</span>
          <div className="w-32"><DateField value={dateFrom} onChange={setDateFrom} testid="mywork-date-from" placeholder="dari" /></div>
          <span className="text-slate-400">–</span>
          <div className="w-32"><DateField value={dateTo} onChange={setDateTo} testid="mywork-date-to" placeholder="sampai" /></div>
        </div>
        {hasFilter && (
          <button
            type="button" onClick={resetFilters} data-testid="mywork-filter-reset"
            className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-slate-600 hover:text-[#FF5A1F]"
          ><RefreshCcw size={11} /> Reset Filter</button>
        )}
      </div>

      {/* Table — 149-cy (PROMPT 16-MW-R2): Fix sticky-header overlap.
          Root cause: `.ens-table-scroll` (overflow-x:auto) membuat browser
          menetapkannya sebagai scroll-ancestor untuk sticky di dalamnya. Akibat:
          thead sticky top:96px terukur dari edge atas wrapper (bukan viewport)
          sehingga header nyangkut di tengah/atas baris data saat halaman
          di-scroll. Fix: pakai wrapper biasa (tanpa overflow-x) → sticky
          reference kembali ke window; tabel apa adanya (max-w-[1600px] cukup
          untuk seluruh kolom pada viewport desktop typical). Kalau viewport
          sempit dan tabel overflow, `html/body { overflow-x: clip }` mencegah
          scrollbar bertumpuk, dan kita berikan min-width pada tabel supaya
          kolom Aksi tetap konsisten. */}
      <div className="bg-white border border-slate-200" data-testid="mywork-table-wrap">
        <table className="w-full text-xs" style={{ minWidth: "1280px" }}>
          <colgroup>
            <col style={{ width: "110px" }} />
            <col style={{ minWidth: "280px" }} />
            <col style={{ minWidth: "160px" }} />
            <col style={{ minWidth: "140px" }} />
            <col style={{ width: "100px" }} />
            <col style={{ width: "100px" }} />
            <col style={{ width: "80px" }} />
            <col style={{ width: "140px" }} />
            <col style={{ minWidth: "280px" }} />
          </colgroup>
          <thead className="ens-sticky-thead">
            <tr className="text-left">
              <th className="px-3 py-2.5 overline text-slate-700 bg-slate-50 border-b-2 border-slate-300 text-left">Tipe</th>
              <th className="px-3 py-2.5 overline text-slate-700 bg-slate-50 border-b-2 border-slate-300 text-left">Judul</th>
              <th className="px-3 py-2.5 overline text-slate-700 bg-slate-50 border-b-2 border-slate-300 text-left">Project</th>
              <th className="px-3 py-2.5 overline text-slate-700 bg-slate-50 border-b-2 border-slate-300 text-left">Pembuat</th>
              <th className="px-3 py-2.5 overline text-slate-700 bg-slate-50 border-b-2 border-slate-300 text-left">Masuk</th>
              <th className="px-3 py-2.5 overline text-slate-700 bg-slate-50 border-b-2 border-slate-300 text-left">Jatuh Tempo</th>
              <th className="px-3 py-2.5 overline text-slate-700 bg-slate-50 border-b-2 border-slate-300 text-left">Prioritas</th>
              <th className="px-3 py-2.5 overline text-slate-700 bg-slate-50 border-b-2 border-slate-300 text-left">Status</th>
              <th className="px-3 py-2.5 overline text-slate-700 bg-slate-50 border-b-2 border-slate-300 text-left">Aksi</th>
            </tr>
          </thead>
          <tbody data-testid="mywork-table">
            {loading && (
              <tr><td colSpan={9} className="px-4 py-6 text-center text-slate-400" data-testid="mywork-loading">Memuat…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-12" data-testid="mywork-empty">
                  <EmptyState tab={currentTab} hasFilter={hasFilter} onResetFilter={resetFilters} />
                </td>
              </tr>
            )}
            {!loading && items.map((it) => (
              <MyWorkRow
                key={`${it.source}-${it.id}`}
                item={it}
                busyId={busyId}
                onAction={handleAction}
              />
            ))}
          </tbody>
        </table>
      </div>

      <ActionConfirmDialog
        state={confirm}
        onClose={() => setConfirm({ open: false, item: null, action: null, note: "" })}
        onConfirm={() => {
          const { item, action, note } = confirm;
          setConfirm({ open: false, item: null, action: null, note: "" });
          if (item && action) runAction(item, action, note);
        }}
      />
    </>
  );
}

// -- moved: previous MyWorkPage closing return } ==============================

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------
function MyWorkRow({ item, busyId, onAction }) {
  const isOverdue = (item.roles || []).includes("overdue");
  return (
    <tr
      data-testid={`mywork-row-${item.source}-${item.id}`}
      className={`border-b border-slate-100 hover:bg-slate-50 ${isOverdue ? "bg-rose-50/40" : ""}`}
    >
      <td className="px-3 py-2.5">
        <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${SOURCE_BADGE[item.source] || "border-slate-300 bg-slate-50 text-slate-700"}`}>
          {item.source_label}
        </span>
        <div className="text-[10px] text-slate-500 mt-0.5">{item.type}</div>
      </td>
      <td className="px-3 py-2.5">
        <div className="font-medium text-[#0B1D3A] truncate max-w-[380px]" title={item.title}>{item.title}</div>
      </td>
      <td className="px-3 py-2.5 text-slate-700">
        {item.project_name || <span className="text-slate-300">—</span>}
        {item.client && <div className="text-[10px] text-slate-500">{item.client}</div>}
      </td>
      <td className="px-3 py-2.5 text-slate-700">{item.creator_name || "-"}</td>
      <td className="px-3 py-2.5 tabular-nums text-slate-600">{item.created_at ? fmtDate(item.created_at) : "-"}</td>
      <td className="px-3 py-2.5 tabular-nums">
        {item.due_date ? (
          <span className={isOverdue ? "text-rose-700 font-semibold" : "text-slate-700"}>{fmtDate(item.due_date)}</span>
        ) : <span className="text-slate-300">—</span>}
      </td>
      <td className="px-3 py-2.5">
        {item.priority ? (
          <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${PRIORITY_TONE[item.priority] || "bg-slate-100 text-slate-600"}`}>{item.priority}</span>
        ) : <span className="text-slate-300">—</span>}
      </td>
      <td className="px-3 py-2.5">
        <span className="text-[10px] font-semibold text-slate-700">{item.status_label || item.status}</span>
        {isOverdue && (
          <div className="text-[9px] text-rose-600 uppercase tracking-wider font-bold mt-0.5"><AlertTriangle size={9} className="inline -mt-px" /> Terlambat</div>
        )}
      </td>
      <td className="px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-1">
          {(item.available_actions || []).map((actionId) => {
            const busy = busyId === (item.id + ":" + actionId);
            return (
              <button
                key={actionId}
                type="button"
                data-testid={`mywork-action-${actionId}-${item.source}-${item.id}`}
                disabled={busy}
                onClick={() => onAction(item, actionId)}
                className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider font-semibold transition-colors disabled:opacity-40 ${ACTION_STYLE[actionId] || ACTION_STYLE.open}`}
              >
                {actionId === "open" && <ExternalLink size={10} />}
                {actionId === "approve" && <Check size={10} />}
                {actionId === "reject" && <X size={10} />}
                {actionId === "revise" && <FileEdit size={10} />}
                {actionId === "done" && <CheckCircle2 size={10} />}
                {busy ? "…" : ACTION_LABELS[actionId] || actionId}
              </button>
            );
          })}
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------
function EmptyState({ tab, hasFilter, onResetFilter }) {
  const Icon = tab.icon;
  return (
    <div className="flex flex-col items-center justify-center text-center gap-2 py-8" data-testid={`mywork-empty-${tab.id}`}>
      <Icon size={32} className="text-slate-300" />
      <div className="text-sm font-semibold text-slate-700">
        {hasFilter ? "Tidak ada item cocok dengan filter" : `Tidak ada item di "${tab.label}"`}
      </div>
      <div className="text-xs text-slate-500 max-w-md">
        {hasFilter
          ? "Coba longgarkan filter atau ubah tipe/prioritas."
          : tab.id === "for_me" ? "Anda sudah bebas dari antrian — semua item yang butuh tindakan sudah selesai."
          : tab.id === "awaiting_approval" ? "Belum ada permintaan yang menunggu persetujuan Anda."
          : tab.id === "overdue" ? "Tidak ada item yang terlambat. Kerja bagus!"
          : tab.id === "created_by_me" ? "Anda belum membuat item apapun."
          : "Belum ada item yang sudah selesai."}
      </div>
      {hasFilter && (
        <button type="button" onClick={onResetFilter} data-testid={`mywork-empty-reset-${tab.id}`}
          className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50">
          <Filter size={11} /> Bersihkan Filter
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Action confirmation dialog (untuk approve/reject/revise dgn optional/required note)
// ---------------------------------------------------------------------------
function ActionConfirmDialog({ state, onClose, onConfirm }) {
  const [note, setNote] = useState("");
  useEffect(() => { if (state.open) setNote(""); }, [state.open]);
  if (!state.open || !state.item || !state.action) return null;
  const title = ACTION_LABELS[state.action] || "Konfirmasi";
  const item = state.item;
  const isReject = state.action === "reject";
  const isRevise = state.action === "revise";
  const noteRequired = isReject || isRevise;
  return (
    <ConfirmDialog
      open={state.open}
      onOpenChange={(open) => { if (!open) onClose(); }}
      onConfirm={() => { state.note = note; onConfirm(); }}
      title={`${title}: ${item.title}`}
      description={
        <div className="space-y-2">
          <div className="text-xs text-slate-600">
            <span className="font-semibold">{item.source_label}</span> · {item.creator_name} · {item.status_label}
          </div>
          <div>
            <label className="overline text-slate-500 text-[10px]">
              Catatan {noteRequired ? <span className="text-rose-600">*</span> : <span className="text-slate-400">(opsional)</span>}
            </label>
            <textarea
              value={note} onChange={(e) => setNote(e.target.value)}
              data-testid="mywork-action-note"
              rows={3}
              placeholder={noteRequired ? "Wajib diisi — alasan singkat" : "Kosongkan bila tidak perlu"}
              className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs focus:outline-none focus:border-[#FF5A1F]"
            />
          </div>
        </div>
      }
      confirmLabel={title}
      confirmDisabled={noteRequired && !note.trim()}
      confirmClass={isReject ? "bg-rose-600 hover:bg-rose-700" : isRevise ? "bg-amber-500 hover:bg-amber-600" : "bg-emerald-600 hover:bg-emerald-700"}
    />
  );
}
